// ONBOARDING-P1 (client-onboarding design §1.4): the client intake the onboarding link opens.
// Business, offers and pricing, team and roles, tools, 90-day goals, pains, what to delegate first,
// recipes and SOPs, availability. Plain data in the engine's shape, so the API checks saves and
// computes what is missing (`missingFields`) with the same engine the guided flows use.
//
// Phase 1 is filled by the owner through the link, before they have an account or a seat, so it
// is not a registered flow (`FLOWS`) and its answers are kept on the onboarding case. Each field's
// binding already names where phase 2 puts it: the CEO seat's collected knowledge.
//
// Client-facing text: written as the HTVA team. No internal agent is named here.
import type { Audience, ChapterDef, FieldDef, FieldType, StepDef } from "./types";

const BOTH: readonly Audience[] = ["assignee", "manager"];

const f = (
  key: string,
  label: string,
  info: string,
  type: FieldType,
  required = false,
  placeholder?: string,
): FieldDef => ({
  key,
  label,
  info,
  type,
  bind: { kind: "collect", usedBy: ["ceo"], memory: "fact" },
  fill: BOTH,
  ...(required ? { required: true } : {}),
  ...(placeholder ? { placeholder } : {}),
  ...(type === "longtext" ? { maxLength: 8000 } : {}),
  ...(type === "text" ? { maxLength: 400 } : {}),
});

const step = (key: string, title: string, info: string, fields: FieldDef[]): StepDef => ({
  key,
  title,
  info,
  fields,
});

export type IntakeDef = {
  key: "collect_onboarding";
  /** Bumped when a question changes (design §5: `collect_onboarding@N`). */
  version: number;
  title: string;
  chapters: readonly ChapterDef[];
};

export const ONBOARDING_INTAKE: IntakeDef = {
  key: "collect_onboarding",
  version: 1,
  title: "Tell us about your business",
  chapters: [
    {
      key: "business",
      title: "Your business",
      steps: [
        step("business", "Business", "What you do and for whom, in your own words.", [
          f(
            "business_summary",
            "What does your business do?",
            "One or two paragraphs: what you sell, to whom, and what makes you different.",
            "longtext",
            true,
          ),
          f(
            "website",
            "Website",
            "So we can read how you describe yourself today.",
            "text",
            false,
            "https://",
          ),
        ]),
        step("offers", "Offers and pricing", "What you sell and what it costs.", [
          f(
            "offers",
            "Your main offers and their prices",
            "One line per offer, with its price or price range.",
            "list",
            true,
          ),
        ]),
        step("team", "Team and roles", "Who works with you today.", [
          f(
            "team",
            "Who is on your team?",
            "One line per person or role: name, role, and what they own.",
            "list",
            true,
          ),
        ]),
        step("tools", "Tools", "The software you run the business on.", [
          f(
            "tools",
            "Which tools do you use?",
            "CRM, email, calendar, project tool, accounting: one per line.",
            "list",
            true,
          ),
        ]),
      ],
    },
    {
      key: "direction",
      title: "Where you are going",
      steps: [
        step("goals", "90-day goals", "What a good next quarter looks like.", [
          f(
            "goals_90d",
            "Your goals for the next 90 days",
            "Up to five, as specific as you can (numbers help).",
            "list",
            true,
          ),
        ]),
        step("pains", "Pains", "Where time and money leak today.", [
          f(
            "pains",
            "What slows you down most?",
            "The tasks that eat your week or keep getting dropped.",
            "longtext",
            true,
          ),
        ]),
        step("delegate", "What to delegate first", "Where help would pay off fastest.", [
          f(
            "delegate_first",
            "What would you hand off first?",
            "The work you would give a new hire on day one.",
            "longtext",
            true,
          ),
        ]),
      ],
    },
    {
      key: "working",
      title: "How you work",
      steps: [
        step("sops", "Recipes and SOPs", "How things are done today, if written down.", [
          f(
            "sops",
            "Links to your SOPs or how-to documents",
            "Optional now: we can also go through them on a call.",
            "links",
          ),
          f(
            "sop_notes",
            "Anything not written down yet",
            "A few lines on how a key task is done today.",
            "longtext",
          ),
        ]),
        step("availability", "Availability", "When we can reach you.", [
          f(
            "availability",
            "When are you available for a setup call?",
            "Days and times, with your time zone.",
            "text",
            true,
          ),
        ]),
      ],
    },
  ],
};
